/* Norwegian storefront: Shopify owns consent; this owner gates theme integrations. */
(() => {
  if (window.ParkShopifyConsent) return;
  let privacy;
  let permissions = {analytics: false, marketing: false, preferences: false};
  let responseCollected = false;
  let initialGoogleSync = false;
  const started = new Set();
  const allowed = category => {
    if (!privacy || window.parkConsentReloadPending) return false;
    return permissions[category] === true;
  };
  const canOpen = () => typeof window.privacyBanner?.showPreferences === 'function';
  const open = () => canOpen() ? window.privacyBanner.showPreferences() : Promise.reject(new Error('Consent banner unavailable'));
  const hasResponse = () => responseCollected || (!!privacy && Object.values(window.Shopify.customerPrivacy.currentVisitorConsent()).some(value => value === 'yes' || value === 'no'));
  window.ParkShopifyConsent = {allowed, canOpen, open, hasResponse};

  function activate() {
    if (!privacy || window.parkConsentReloadPending) return;
    if ([...started].some(category => !allowed(category))) {
      window.parkConsentReloadPending = true;
      window.location.reload();
      return;
    }
    document.querySelectorAll('template[data-park-consent]').forEach(template => {
      const categories = template.dataset.parkConsent.split(' ');
      if (!categories.every(allowed)) return;
      categories.forEach(category => started.add(category));
      // Merchant markup stays intact and inert until every required purpose is allowed.
      const fragment = template.content.cloneNode(true);
      fragment.querySelectorAll('script').forEach(old => {
        const script = document.createElement('script');
        [...old.attributes].forEach(attribute => script.setAttribute(attribute.name, attribute.value));
        script.textContent = old.textContent;
        old.replaceWith(script);
      });
      template.replaceWith(fragment);
    });
  }
  function sync() {
    if (!privacy) return;
    // The existing EditionConsent controller owns subsequent Google updates.
    // On a returning visit its event does not fire: initialize from Shopify, not cached localStorage.
    if (!initialGoogleSync) {
      initialGoogleSync = true;
      const status = category => allowed(category) ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        functionality_storage: 'granted', analytics_storage: status('analytics'),
        personalization_storage: status('analytics'), ad_storage: status('marketing'),
        ad_user_data: status('marketing'), ad_personalization: status('marketing')
      });
    }
    activate();
    window.dispatchEvent(new Event('park:consent-changed'));
  }
  // EditionConsent finishes its asynchronous event handler before integrations start.
  document.addEventListener('visitorConsentCollected', event => {
    permissions = {analytics: event.detail.analyticsAllowed === true, marketing: event.detail.marketingAllowed === true, preferences: event.detail.preferencesAllowed === true};
    responseCollected = true;
    queueMicrotask(sync);
  });
  function readPermissions() {
    privacy = window.Shopify.customerPrivacy;
    if (!privacy) return;
    permissions = {analytics: privacy.analyticsProcessingAllowed(), marketing: privacy.marketingAllowed(), preferences: privacy.preferencesProcessingAllowed()};
    sync();
  }
  window.addEventListener('pageshow', readPermissions);
  document.addEventListener('DOMContentLoaded', sync, {once: true});
  window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.2'}], error => {
    if (error) { console.warn('PARK consent API could not load', error); return; }
    readPermissions();
  });

  if (!customElements.get('park-consent-settings')) {
    customElements.define('park-consent-settings', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.events = new AbortController();
        const options = {signal: this.events.signal};
        const ready = () => { this.button.disabled = !canOpen(); };
        this.button.addEventListener('click', async () => {
          if (!canOpen() || this.opened) return;
          this.returnFocus = this.prepareOpen?.() || this.button;
          this.opened = true;
          try {
            await open();
            if (!this.isConnected) return;
            this.watchDialog();
          } catch (_) { this.opened = false; ready(); }
        }, options);
        window.addEventListener('park:consent-changed', ready, options);
        window.addEventListener('load', ready, options);
        ready();
      }
      watchDialog() {
        const dialog = document.querySelector('#shopify-pc__prefs');
        if (!dialog) { this.opened = false; return; }
        this.observer?.disconnect();
        const restore = () => {
          this.observer?.disconnect();
          this.opened = false;
          const active = document.activeElement;
          if (this.isConnected && this.returnFocus?.isConnected && !window.parkConsentReloadPending &&
              (!active || active === document.body || dialog.contains(active))) this.returnFocus.focus({preventScroll: true});
        };
        this.observer = new MutationObserver(() => {
          if (!dialog.isConnected || !dialog.getClientRects().length) restore();
        });
        this.observer.observe(dialog.parentNode, {childList: true});
        this.observer.observe(dialog, {attributes: true, attributeFilter: ['style', 'hidden', 'aria-hidden']});
      }
      disconnectedCallback() {
        this.events?.abort(); this.events = null;
        this.observer?.disconnect(); this.opened = false;
      }
    });
  }
})();
